import { useState } from "react";
import { BlockTitle } from "@blocks/Blocks";
import Ad from "@components/Advertisement/Ad";
import AdSettings from "@components/Advertisement/AdSettings";
import Container from "@components/Container/Container";
import GAData from "@components/GAData/GAData";
import IconFactory from "@components/Icon/IconFactory";
import { IconTypeEnum } from "@components/Icon/utils/types";
import LoadMore from "@components/LoadMore/LoadMore";
import MetaTags from "@components/MetaTags/MetaTags";
import EventsAwardsLogoMenu from "@events-awards/components/EventsAwardsLogoMenu";
import EventsAwardsHeader from "@events-awards/EventAwardsHeader";
import { AWARDS_EVENTS_PAGE_ROW_DATA } from "@events-awards/utils/constants";
import {
  AwardDataRow,
  EventsAwardsWinnersContextData,
} from "@events-awards/utils/types";
import { useLoadMoreEventsWinners } from "@hooks/useLoadMoreEventsWinners";
import Paragraph from "@pages/Article/components/StoryElements/Paragraph";

import SIAFooter from "./SIAFooter";

/**
 * Component to render SIA winners page content
 */
const EventsAwardsSIAWinnerPage = ({
  articles,
  awardType,
}: EventsAwardsWinnersContextData): React.ReactElement => {
  const { articleList, handleLoadMore, hasMoreArticles } =
    useLoadMoreEventsWinners(articles, 10, awardType);
  const [showTabs, setShowTabs] = useState(false);

  const years = articleList.map(({ title }) => title);
  const [selectedYear, setSelectedYear] = useState(years[0]);

  const awardData: AwardDataRow | undefined = AWARDS_EVENTS_PAGE_ROW_DATA.find(
    (data) => data.awardType === awardType
  );
  const awardPath = awardData?.blockTitleProps?.link || "";

  return (
    <div
      className="min-h-screen w-full"
      data-testid="events-awards-sia-winners-page"
    >
      <AdSettings
        adNames={["lb1", "prestitial", "catfish", "abm"]}
        path={`${awardPath}/winners`}
        adTargets={[{ key: "page", value: "listing" }]}
      />

      <GAData
        title={`${awardData?.blockTitleProps?.text} Winners`}
        level2="events awards"
      />

      <MetaTags
        title="THE BUSINESS TIMES - Get the Latest Business & Financial News"
        description={`The Business Times ${awardData?.blockTitleProps?.text} - ${awardData?.description}`}
        ogType="website"
        slug={awardPath}
      />

      <EventsAwardsHeader />

      <Container>
        <EventsAwardsLogoMenu awardType={awardType} />

        <div className="-mx-0 w-full bg-gray-150 px-6 py-4 font-public-sans">
          <div>
            <button
              onClick={() => {
                setShowTabs(!showTabs);
              }}
              className="mb-2 flex items-center gap-2 text-left text-2xl font-bold focus:outline-none"
            >
              <BlockTitle
                text={selectedYear}
                rootClassName="text-4xl md:text-6xl font-medium lg:text-8xl hover:underline"
              />

              <IconFactory
                type={IconTypeEnum.arrowDown}
                x={30}
                y={23}
                size={50}
              />
            </button>
          </div>

          {showTabs ? (
            <div className="mb-2 flex flex-wrap gap-4 border border-gray-200 bg-white p-2 shadow-sm">
              {years.map((year) => (
                <button
                  key={year}
                  onClick={() => {
                    setSelectedYear(year);
                    setShowTabs(false);
                  }}
                  className={`px-4 py-2 text-base font-semibold transition-colors ${
                    selectedYear === year
                      ? "bg-gray-650 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {year}
                </button>
              ))}
            </div>
          ) : null}

          <div className="border-t border-gray-100 py-6 text-left">
            {articleList.find((article) => article.title === selectedYear)
              ?.elements &&
              (
                articleList.find((article) => article.title === selectedYear)
                  ?.elements || []
              ).map((element, index) => (
                <Paragraph
                  key={index}
                  element={element}
                  className="mb-0 text-gray-850"
                />
              ))}
          </div>

          {hasMoreArticles ? (
            <LoadMore
              onLoadMore={handleLoadMore}
              hasMore={hasMoreArticles}
              loadText="MORE"
            />
          ) : null}
        </div>
      </Container>

      <SIAFooter />

      <Ad adType="catfish" />

      <Ad adType="abm" />

      <Ad adType="prestitial" />
    </div>
  );
};

export default EventsAwardsSIAWinnerPage;
